import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Input, Drawer, Select, Tag, Alert } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { useMercuryContext } from "@context/index";
import { successfulNotification, failedNotification } from "common/utils/notifications";
import { handleApiError } from "common/utils/handleApiError";
import { applyRestValidationError, applyRestDuplicateKeyError } from "common/utils/validationErrors";
import { useDrawerScrollMargin } from "common/utils/useScrollPosition";
import "./worklog-definitions.scss";

type Mode = "create" | "edit" | "delete" | "restore";

export interface WorklogDefinition {
  id?: string;
  name: string;
  description?: string;
  dbKey: string;
  targetEntity?: "DOCUMENT" | "PLP";
  projectTypes: string[];
  active: boolean;
  deleted: boolean;
  userDefined?: boolean;
}

const MONGO_FIELD_NAME_REGEX = /^[A-Za-z_][A-Za-z0-9_]*$/;

const DB_KEY_MAX_LENGTH = 64;

const RESERVED_DB_KEY_NAMES = new Set([
  "_id",
  "__proto__",
  "constructor",
  "prototype",
]);

const toMongoCamelCase = (input: string): string => {
  if (!input) return "";
  const words = input
    .replace(/[^A-Za-z0-9]+/g, " ")
    .trim()
    .split(/\s+/);
  if (words.length === 0 || (words.length === 1 && words[0] === "")) return "";
  let result = words
    .map((word, i) => {
      const lower = word.toLowerCase();
      if (i === 0) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join("");
  if (/^[0-9]/.test(result)) result = "_" + result;
  return result.slice(0, DB_KEY_MAX_LENGTH);
};

interface WorklogDefinitionFormProps {
  mode?: Mode;
  editData?: WorklogDefinition | null;
  refreshList?: () => void;
  disabled?: boolean;
}

const actionText: Record<Mode, string> = {
  create: "Create",
  edit: "Edit",
  delete: "Delete",
  restore: "Restore",
};

const WorklogDefinitionForm: React.FC<WorklogDefinitionFormProps> = ({
  mode = "create",
  editData,
  refreshList,
  disabled = false,
}) => {
  const { invoicingClient, gpClient } = useMercuryContext();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [projectTypeOptions, setProjectTypeOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [dbKeyManuallyEdited, setDbKeyManuallyEdited] = useState(false);
  const [form] = Form.useForm<WorklogDefinition>();
  const isDrawerMode = mode === "edit" || mode === "create";
  const autoDeriveDbKey = mode === "create";
  // userDefined=false is set manually outside the DB for definitions whose
  // values are auto-updated by business logic; only name/description are editable.
  const isSystemManaged = mode === "edit" && editData?.userDefined === false;

  useDrawerScrollMargin("", open);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await gpClient.get(
          "/google-gp-v1/project/listFields/projectType"
        );
        if (cancelled) return;
        setProjectTypeOptions(
          data.map((item: any) => ({ value: item.value, label: item.label }))
        );
      } catch (error) {
        handleApiError(error, "Failed to load project types");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, gpClient]);

  useEffect(() => {
    if (!open) return;

    if (editData) {
      form.setFieldsValue({
        ...editData,
        active: editData.active ?? true,
      });
      setDbKeyManuallyEdited(true);
    } else {
      form.resetFields();
      form.setFieldsValue({ active: true });
      setDbKeyManuallyEdited(false);
    }
  }, [open, editData, form]);

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    setOpen(false);
    form.resetFields();
  };

  const handleSubmit = async (values: WorklogDefinition) => {
    try {
      setLoading(true);

      if ((mode === "delete" || mode === "restore") && editData?.id) {
        await invoicingClient.patch(
          `/invoicing-v1/worklog-type-definitions/${editData.id}`,
          {
            deleted: mode === "delete",
          }
        );

        successfulNotification(
          mode === "delete"
            ? "Worklog definition deleted"
            : "Worklog definition restored"
        );
      } else if (editData?.id) {
        // System-managed definitions (userDefined=false) only allow editing
        // name/description; don't resend the business-logic-managed fields.
        const payload = isSystemManaged
          ? { name: values.name, description: values.description }
          : values;
        await invoicingClient.patch(
          `/invoicing-v1/worklog-type-definitions/${editData.id}`,
          payload
        );
        successfulNotification("Worklog definition updated");
      } else {
        const payload = {
          clientId: "google-projects",
          ...values,
          deleted: false,
        };
        await invoicingClient.post(
          `/invoicing-v1/worklog-type-definitions/`,
          payload
        );
        successfulNotification("Worklog definition created");
      }

      refreshList?.();
      handleClose();
    } catch (error: any) {
      const formLevel = applyRestValidationError(error, values, form);
      if (formLevel !== null) {
        if (formLevel.length) failedNotification("Error", formLevel.join(" "));
        return; // handled
      }
      if (
        applyRestDuplicateKeyError(error, form, {
          field: "dbKey",
          message: "This DB Key already exists. Please choose a unique key.",
        })
      ) {
        return;
      }
      handleApiError(error, "Failed to save Worklog definition");
    } finally {
      setLoading(false);
    }
  };

  const onPreventMouseDown = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const customTagRender = ({ label, closable, onClose }: any) => {
    return (
      <Tag
        color="var(--tertiary-color)"
        onMouseDown={onPreventMouseDown}
        closable={closable}
        onClose={onClose}
        style={{ marginRight: 3, marginBottom: 3 }}
      >
        {label}
      </Tag>
    );
  };

  const WorklogDefinitionFormContent = (
    <Form<WorklogDefinition>
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{ active: true }}
      onValuesChange={(changedValues) => {
        if (
          autoDeriveDbKey &&
          !dbKeyManuallyEdited &&
          Object.prototype.hasOwnProperty.call(changedValues, "name")
        ) {
          form.setFieldValue("dbKey", toMongoCamelCase(changedValues.name ?? ""));
        }
      }}
    >
      {isSystemManaged && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="This worklog definition is managed by the system. Its value is updated automatically by business logic, so only the name and description can be edited."
        />
      )}
      <Form.Item
        name="name"
        label="Name"
        rules={[{ required: true, message: "Name is required" }]}
      >
        <Input />
      </Form.Item>

      <Form.Item name="description" label="Description">
        <Input.TextArea rows={2} />
      </Form.Item>

      <Form.Item
        name="dbKey"
        label="DB Key"
        tooltip={`The MongoDB field name used to store this worklog value. Must start with a letter or underscore and contain only letters, digits, and underscores (case-sensitive). No dots, spaces, or special characters. Max ${DB_KEY_MAX_LENGTH} characters. Reserved names (_id, __proto__, constructor, prototype) are not allowed.`}
        rules={[
          { required: true, message: "DB Key is required" },
          {
            max: DB_KEY_MAX_LENGTH,
            message: `Must be ${DB_KEY_MAX_LENGTH} characters or fewer`,
          },
          {
            validator: (_, value) => {
              if (!value) return Promise.resolve();
              if (!MONGO_FIELD_NAME_REGEX.test(value)) {
                return Promise.reject(
                  new Error(
                    "Must be a valid MongoDB field name (case-sensitive letters, digits, underscores; no dots; cannot start with a digit or '$')"
                  )
                );
              }
              if (RESERVED_DB_KEY_NAMES.has(value)) {
                return Promise.reject(
                  new Error(`"${value}" is a reserved name`)
                );
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <Input
          maxLength={DB_KEY_MAX_LENGTH}
          placeholder="e.g. dtpServiceHours"
          disabled={isSystemManaged}
          onChange={() => {
            if (autoDeriveDbKey) setDbKeyManuallyEdited(true);
          }}
        />
      </Form.Item>

      <Form.Item
        name="targetEntity"
        label="Target Entity"
        rules={[{ required: true, message: "Target Entity is required" }]}
      >
        <Select
          placeholder="Select a Target Entity"
          disabled={isSystemManaged}
          options={[
            { value: "PLP", label: "PLP" },
            { value: "DOCUMENT", label: "Document" },
          ]}
        />
      </Form.Item>

      <Form.Item
        name="projectTypes"
        label="Project Types"
        rules={[{ required: true, message: "This field is required" }]}
      >
        <Select
          mode="multiple"
          allowClear
          disabled={isSystemManaged}
          placeholder="Project Types"
          options={projectTypeOptions}
          filterOption={(inputValue: any, option: any) =>
            option.label
              .toUpperCase()
              .indexOf(inputValue.toUpperCase()) !== -1
          }
          tagRender={customTagRender}
        />
      </Form.Item>
    </Form>
  );

  return (
    <>
      <Button
        icon={
          mode === "edit" ? (
            <EditOutlined />
          ) : mode === "delete" ? (
            <DeleteOutlined />
          ) : mode === "restore" ? (
            <UndoOutlined />
          ) : (
            <PlusOutlined />
          )
        }
        type={mode === "create" ? "primary" : "link"}
        danger={mode === "delete"}
        size={mode === "create" ? "middle" : "small"}
        onClick={handleOpen}
        disabled={disabled}
      >
        {mode === "create" ? "Add Worklog Definition" : actionText[mode]}
      </Button>

      {isDrawerMode && (
        <Drawer
          title={`${actionText[mode]} Worklog Definition`}
          open={open}
          onClose={handleClose}
          width={480}
          destroyOnClose
          maskClosable={!loading}
          closable={!loading}
          className="worklog-definitions"
          footer={
            <div style={{ marginBottom: 120 }}>
              <Button
                type="primary"
                loading={loading}
                onClick={() => form.submit()}
                style={{ marginLeft: 8 }}
              >
                {mode === "edit" ? "Save" : actionText[mode]} Worklog
                Definition
              </Button>
            </div>
          }
        >
          {WorklogDefinitionFormContent}
        </Drawer>
      )}

      {!isDrawerMode && (
        <Modal
          title={`${actionText[mode]} Worklog Definition`}
          open={open}
          onCancel={handleClose}
          onOk={() => handleSubmit({} as WorklogDefinition)}
          confirmLoading={loading}
          destroyOnClose
          okText={actionText[mode]}
          maskClosable={!loading}
          closable={!loading}
        >
          {mode === "delete"
            ? "Are you sure you want to delete this Worklog definition?"
            : "Are you sure you want to restore this Worklog definition?"}
        </Modal>
      )}
    </>
  );
};

export default WorklogDefinitionForm;
